import React, { useState, useRef, useEffect, useCallback } from "react";
import './styles.css'; // move your inline styles here if desired
import mapWvImage from '../../images/WestVirginia.svg';
import Dropdown from '../dropdown/dropdown';
import config from '../../config/appConfig';
import { apicFetch } from '../../services/apiTokenService';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';

// Query string sent to the provider search/retrieve API when a county is
// selected via either the dropdown or the map. Exported so the parent
// Search page can use the exact same query text when navigating to the
// Results page after a CSED county selection.
export const CSED_QUERY = "Children with serious emotional disorder";

// Approximate centroid latitude/longitude for each West Virginia county,
// used as the lat/long POST body inputs for the provider search/retrieve
// API call triggered by the dropdown and the map. Exported so the parent
// Search page can look up the same coordinates when navigating to Results.
export const COUNTY_COORDINATES = {
  Barbour: { lat: 39.1414, long: -79.9903 },
  Berkeley: { lat: 39.4567, long: -78.0067 },
  Boone: { lat: 38.0198, long: -81.7220 },
  Braxton: { lat: 38.6807, long: -80.7770 },
  Brooke: { lat: 40.3287, long: -80.5570 },
  Cabell: { lat: 38.3946, long: -82.2554 },
  Calhoun: { lat: 38.8451, long: -81.1198 },
  Clay: { lat: 38.4671, long: -81.0870 },
  Doddridge: { lat: 39.2724, long: -80.7250 },
  Fayette: { lat: 38.0637, long: -81.1096 },
  Gilmer: { lat: 38.9192, long: -80.8362 },
  Grant: { lat: 39.0731, long: -79.1462 },
  Greenbrier: { lat: 37.9276, long: -80.4720 },
  Hampshire: { lat: 39.3260, long: -78.6470 },
  Hancock: { lat: 40.5340, long: -80.5760 },
  Hardy: { lat: 38.9370, long: -78.8790 },
  Harrison: { lat: 39.2670, long: -80.3540 },
  Jackson: { lat: 38.9450, long: -81.6790 },
  Jefferson: { lat: 39.3260, long: -77.8590 },
  Kanawha: { lat: 38.3860, long: -81.4890 },
  Lewis: { lat: 38.9660, long: -80.4470 },
  Lincoln: { lat: 38.1810, long: -82.0290 },
  Logan: { lat: 37.8590, long: -81.9950 },
  Marion: { lat: 39.4870, long: -80.1230 },
  Marshall: { lat: 39.8930, long: -80.6360 },
  Mason: { lat: 38.7660, long: -82.0330 },
  McDowell: { lat: 37.3810, long: -81.5540 },
  Mercer: { lat: 37.4030, long: -81.1200 },
  Mineral: { lat: 39.4110, long: -78.9640 },
  Mingo: { lat: 37.6980, long: -82.1080 },
  Monongalia: { lat: 39.6420, long: -79.9550 },
  Monroe: { lat: 37.5540, long: -80.5520 },
  Morgan: { lat: 39.5980, long: -78.2280 },
  Nicholas: { lat: 38.2170, long: -80.7830 },
  Ohio: { lat: 40.1120, long: -80.6960 },
  Pendleton: { lat: 38.6810, long: -79.3560 },
  Pleasants: { lat: 39.3560, long: -81.1420 },
  Pocahontas: { lat: 38.3800, long: -79.9500 },
  Preston: { lat: 39.4870, long: -79.6480 },
  Putnam: { lat: 38.4990, long: -81.9490 },
  Raleigh: { lat: 37.7830, long: -81.2000 },
  Randolph: { lat: 38.7440, long: -79.9110 },
  Ritchie: { lat: 39.1990, long: -81.0630 },
  Roane: { lat: 38.7660, long: -81.3660 },
  Summers: { lat: 37.6890, long: -80.8770 },
  Taylor: { lat: 39.3400, long: -80.0080 },
  Tucker: { lat: 39.1230, long: -79.6260 },
  Tyler: { lat: 39.4520, long: -80.9330 },
  Upshur: { lat: 38.9010, long: -80.2140 },
  Wayne: { lat: 38.1240, long: -82.4110 },
  Webster: { lat: 38.5010, long: -80.4260 },
  Wetzel: { lat: 39.6340, long: -80.6700 },
  Wirt: { lat: 39.0330, long: -81.3980 },
  Wood: { lat: 39.2110, long: -81.5240 },
  Wyoming: { lat: 37.5570, long: -81.5350 }
};

// The MAP_AREAS coordinates below are authored against a 676 x 608 base size
// (matching the WestVirginia.svg artwork's actual intrinsic size — see the
// SVG's own width/height attributes). This must stay in sync with the real
// asset: the previous value here (550) didn't match the SVG's real height
// (608), so scale.y (renderedHeight / BASE_MAP_HEIGHT) was computed against
// the wrong denominator and came out too large. That over-scaled every
// polygon/area's y-coordinate by the same proportional amount, which is
// invisible for small y values near the top of the map but compounds for
// larger y values further down — producing exactly the "hover state starts
// slightly shifted and the gap grows further down the map" symptom. To keep
// the clickable polygons aligned with the displayed (and potentially
// resized/responsive) image, we scale the coordinates at render time based
// on the image's current rendered size.
const BASE_MAP_WIDTH = 676;
const BASE_MAP_HEIGHT = 608;

// List of WV counties for the dropdown
const COUNTIES = [
  "Barbour", "Berkeley", "Boone", "Braxton", "Brooke", "Cabell", "Calhoun",
  "Clay", "Doddridge", "Fayette", "Gilmer", "Grant", "Greenbrier", "Hampshire",
  "Hancock", "Hardy", "Harrison", "Jackson", "Jefferson", "Kanawha", "Lewis",
  "Lincoln", "Logan", "Marion", "Marshall", "Mason", "McDowell", "Mercer",
  "Mineral", "Mingo", "Monongalia", "Monroe", "Morgan", "Nicholas", "Ohio",
  "Pendleton", "Pleasants", "Pocahontas", "Preston", "Putnam", "Raleigh",
  "Randolph", "Ritchie", "Roane", "Summers", "Taylor", "Tucker", "Tyler",
  "Upshur", "Wayne", "Webster", "Wetzel", "Wirt", "Wood", "Wyoming"
];

// Dropdown component expects an array of { value, label } options rather than
// raw strings.
const COUNTY_OPTIONS = COUNTIES.map((county) => ({ value: county, label: county }));

// Image-map area coordinates and titles for each county. These coordinates
// have been rescaled from the original 450x450 authoring size up to the
// current 676x550 image size (fx = 676/450, fy = 550/450) so they still
// align with the WestVirginia.svg artwork at its current intrinsic size.
const MAP_AREAS = [
  { alt: "Hancock", coords: "290,2,290,42,280,42,282,29,279,27,278,18,273,12,277,5,285,7" },
  { alt: "Putnam", coords: "82,364,94,355,123,351,129,361,122,366,121,378,126,386,102,397,98,412,91,418,83,417,84,411" },
  { alt: "Brooke", coords: "279,46,291,45,290,84,276,82,270,81,275,73,281,68,281,58,282,51" },
  { alt: "Ohio", coords: "291,88,291,110,264,108,268,89,270,81,274,86,282,86" },
  { alt: "Marshall", coords: "291,112,290,164,251,164,246,158,252,152,252,145,257,137,254,132,260,131,259,125,263,119,263,111" },
  { alt: "Wetzel", coords: "250,167,303,166,307,170,307,179,299,179,303,183,296,186,293,193,295,198,295,205,292,210,290,216,283,212,276,210,268,208,262,203,257,196,251,191,246,185,237,186,243,182,246,176" },
  { alt: "Monongalia", coords: "308,167,310,178,315,181,321,184,344,186,360,201,367,208,374,213,376,206,390,182,391,175,394,169,394,166" },
  { alt: "Preston", coords: "397,167,395,174,392,180,391,186,379,208,379,239,383,242,385,249,390,251,406,245,425,252,433,257,434,167" },
  { alt: "Marion", coords: "305,183,312,183,318,185,340,188,356,201,361,207,368,213,354,223,345,221,337,221,328,218,298,209,299,201,299,194,299,190" },
  { alt: "Tyler", coords: "235,189,240,191,248,189,250,195,255,198,259,202,263,207,268,209,277,213,268,215,263,218,261,222,248,232,241,238,243,231,239,224,234,220,231,232,226,231,224,211,218,207,212,211,221,200,228,192" },
  { alt: "Doddridge", coords: "239,244,249,234,263,226,266,218,274,217,281,217,287,218,287,225,288,230,284,238,278,241,284,248,284,255,289,258,281,261,278,267,270,270,268,276,252,273" },
  { alt: "Harrison", coords: "295,211,326,219,334,225,335,243,340,252,332,262,331,273,325,275,302,270,298,263,292,267,285,263,292,258,287,254,290,252,286,249,285,243,284,241,290,233,289,226,291,220,293,217" },
  { alt: "Taylor", coords: "342,253,334,264,333,271,336,275,342,279,347,286,356,287,357,296,356,302,376,298,381,279,385,274,383,267,385,262,387,255,382,249,379,242,370,243,364,251,352,254" },
  { alt: "Mineral", coords: "501,248,510,244,515,234,520,232,526,222,528,215,537,209,542,202,546,197,538,195,532,191,530,188,537,185,532,180,524,195,508,212,504,217,493,209,488,214,481,221,476,225,468,231,465,235,487,238" },
  { alt: "Morgan", coords: "607,222,614,202,619,187,624,187,629,184,634,180,624,175,614,171,606,175,601,185,591,181,585,184,580,185,582,190,575,190,580,197,574,201,578,205" },
  { alt: "Berkeley", coords: "633,244,608,224,633,245,644,224,655,216,659,206,655,197,650,194,658,192,655,186,648,188,643,192,642,185,637,188,633,183,628,188,625,191,620,189,615,201,608,225" },
  { alt: "Jefferson", coords: "634,246,660,268,664,260,669,249,670,241,672,238,673,229,671,220,665,218,665,215,665,209,665,207,653,222,645,229" },
  { alt: "Hampshire", coords: "506,252,518,258,534,263,543,265,549,269,555,267,561,267,568,275,574,272,580,263,578,256,581,251,579,246,589,234,585,229,589,226,588,214,572,204,565,202,559,202,552,201,547,200,540,211,532,218,525,230,518,236" },
  { alt: "Hardy", coords: "503,253,515,259,542,268,547,272,553,272,559,270,566,276,560,286,562,290,555,298,551,295,541,306,536,303,529,313,519,334,503,319,493,334,484,326,490,317,495,305,491,293,491,281,496,275,496,268,500,263" },
  { alt: "Grant", coords: "463,238,482,239,502,252,498,260,494,267,493,273,488,280,488,286,488,292,490,300,491,307,487,316,482,325,455,303,459,289,460,285,462,280,455,276,457,270,457,267,461,263,460,256,452,254,445,258,439,258,437,258,445,250,454,243" },
  { alt: "Tucker", coords: "406,247,389,254,388,262,387,268,388,273,393,274,395,282,395,288,430,297,437,297,445,298,451,298,456,289,458,280,451,277,454,269,458,258,454,259,448,261,442,261,440,263,432,258,425,254" },
  { alt: "Barbour", coords: "341,253,363,251,371,243,379,242,382,250,387,255,385,266,384,273,378,296,357,300,357,287,347,286,341,278,334,274,333,263" },
  { alt: "Pleasants", coords: "177,233,188,227,195,225,200,219,210,213,220,212,224,230,218,234,210,235,204,238,196,239,191,243,188,238,184,239" },
  { alt: "Wood", coords: "166,221,174,234,178,238,182,241,190,245,183,261,169,268,146,287,125,276,126,270,124,264,128,258,132,257,132,248,148,247,150,235,158,228" },
  { alt: "Ritchie", coords: "235,227,233,234,224,233,218,237,212,238,206,240,197,242,191,247,184,267,189,272,192,277,195,286,201,283,204,286,211,284,213,288,221,291,229,285,236,284,244,278,250,274,237,246,239,239,239,231,236,232" },
  { alt: "Wirt", coords: "182,266,184,271,189,277,189,284,194,288,201,287,187,302,187,308,179,311,165,302,157,304,153,299,155,292,148,288,169,270" },
  { alt: "Jackson", coords: "114,281,120,281,124,277,153,293,151,299,155,306,152,311,154,328,150,354,153,362,142,370,137,365,124,348,116,343,119,341,105,316,109,316,112,303,122,307,121,300,121,290" },
  { alt: "Roane", coords: "159,306,164,304,177,311,188,310,192,314,194,324,194,331,199,336,202,344,204,355,212,361,201,375,192,376,186,374,162,372,156,363,152,353,156,330,155,313,160,309" },
  { alt: "Mason", coords: "84,292,91,292,98,297,101,302,97,312,114,339,116,347,96,351,81,361,79,384,62,367,68,364,65,352,65,346,60,333,71,324,71,313,78,298" },
  { alt: "Cabell", coords: "25,398,45,393,52,369,59,367,79,386,81,401,66,408,58,414,53,426,49,422,53,415,48,408,43,414" },
  { alt: "Wayne", coords: "2,441,7,437,6,428,12,423,10,411,9,399,21,399,41,415,47,413,46,418,49,427,50,436,52,445,59,460,35,490,29,486,22,482,25,472" },
  { alt: "Lincoln", coords: "53,476,93,462,93,452,97,444,102,443,103,436,108,436,115,424,111,417,101,416,91,422,81,420,80,406,64,413,58,421,53,428,52,444,65,461" },
  { alt: "Mingo", coords: "34,495,49,477,61,474,65,481,71,489,70,496,73,507,77,517,82,522,90,534,97,525,103,525,113,530,112,538,107,545,97,551,86,547,80,548,72,544,71,537,62,531,56,529,50,525,48,510,42,506,34,500,34,499,34,500" },
  { alt: "Logan", coords: "65,474,94,467,94,473,99,476,113,479,119,487,122,495,126,501,135,498,139,504,123,509,126,520,116,526,107,524,99,525,91,532,85,522,78,517,77,508,73,500,74,489,68,481" },
  { alt: "Wyoming", coords: "126,510,142,506,147,510,154,506,162,508,182,541,184,547,192,551,187,559,182,562,185,569,174,563,165,562,159,559,152,557,140,552,131,551,127,556,119,554,113,549,111,546,118,537,118,528,131,523" },
  { alt: "Raleigh", coords: "95,470,101,462,94,458,99,443,105,444,107,437,112,437,118,433,125,436,128,430,135,430,137,437,148,445,152,450,160,461,162,470,154,471,147,478,148,491,149,499,152,503,148,507,137,496,128,501,122,491,123,482,112,477,103,475" },
  { alt: "Summers", coords: "215,539,218,532,228,524,231,518,239,523,241,526,242,517,242,509,237,500,256,493,268,508,273,518,246,564" },
  { alt: "Monroe", coords: "276,519,304,525,308,521,312,524,317,524,320,528,326,531,332,534,319,543,317,550,324,551,314,559,300,567,293,559,268,573,260,575,257,570,247,567" },
  { alt: "McDowell", coords: "110,549,112,554,126,556,132,552,140,554,150,559,158,561,164,563,171,565,182,569,173,590,156,599,152,604,137,605,126,596,117,592,109,585,101,577,101,569,93,560,102,551" },
  { alt: "Mercer", coords: "178,584,185,571,190,573,187,565,192,557,198,553,201,549,202,544,206,540,214,540,244,567,241,574,246,582,232,591,227,588,223,592,208,592,197,599" },
  { alt: "Greenbrier", coords: "304,424,311,430,312,449,327,461,340,462,351,460,367,458,363,470,346,486,340,489,339,496,332,502,332,509,326,512,330,516,321,523,317,517,313,522,308,516,303,523,276,515,273,509,259,495,263,495,255,477,259,471,254,460,252,456,250,458,243,454,244,453,268,457" },
  { alt: "Fayette", coords: "194,424,202,428,208,429,217,436,221,429,229,432,234,439,240,448,240,454,247,461,256,469,252,477,259,490,235,497,230,495,225,498,217,495,217,490,185,487,184,479,181,473,176,471,182,440,184,442" },
  { alt: "Boone", coords: "95,470,101,462,94,458,99,443,105,444,107,437,112,437,118,433,125,436,128,430,135,430,137,437,148,445,152,450,160,461,162,470,154,471,147,478,148,491,149,499,152,503,148,507,137,496,128,501,122,491,123,482,112,477,103,475" },
  { alt: "Kanawha", coords: "129,361,137,370,152,363,161,373,185,375,197,377,185,385,192,421,181,438,173,473,164,471,166,467,161,459,157,459,152,454,156,450,152,444,144,440,137,430,128,426,123,429,114,430,119,423,114,415,102,411,103,400,129,389,126,381,123,375,124,369" },
  { alt: "Clay", coords: "189,386,207,372,221,354,229,363,235,370,240,377,250,386,237,396,196,421" },
  { alt: "Pocahontas", coords: "393,341,397,343,398,352,406,354,409,349,414,354,410,368,407,374,409,378,404,383,407,392,402,399,398,405,392,408,390,416,391,421,373,438,376,442,370,447,373,450,367,455,351,457,340,460,329,460,315,447,316,411,320,412,348,388,361,391,383,374,384,368,388,356" },
  { alt: "Webster", coords: "272,381,295,356,301,342,309,343,315,342,319,348,326,349,337,378,329,400,318,408,313,408,313,426,280,405,278,407,275,399,278,398" },
  { alt: "Randolph", coords: "380,299,383,283,388,274,391,279,393,284,393,289,426,299,436,300,442,299,450,302,446,308,438,306,435,312,428,314,426,321,429,327,423,341,418,346,415,351,408,348,407,350,399,349,399,340,392,338,390,342,384,362,381,366,380,372,360,388,348,386,332,397,340,378,327,347,332,341,346,339,351,323,350,312,352,305" },
  { alt: "Upshur", coords: "324,278,330,277,339,279,345,288,355,289,354,296,351,302,348,309,347,320,347,328,344,337,332,337,324,346,319,342,311,338,309,337,309,326,313,315,317,308,320,304,320,296,322,288" },
  { alt: "Lewis", coords: "284,266,289,269,296,269,320,277,319,289,317,295,317,300,313,305,310,316,307,326,307,340,303,333,300,328,299,323,292,319,284,317,281,311,266,291,264,279,269,279,273,271" },
  { alt: "Gilmer", coords: "224,292,232,288,239,287,244,281,252,275,261,278,263,292,277,310,269,311,230,340,225,334,221,330,226,326,227,313,227,303" },
  { alt: "Calhoun", coords: "204,289,210,287,213,291,220,293,224,303,223,315,224,323,218,329,221,336,227,342,215,358,206,356,204,348,204,341,201,333,199,331,198,319,194,312,189,305" },
  { alt: "Braxton", coords: "224,351,230,343,269,314,275,317,280,313,282,319,289,321,296,325,299,332,301,337,293,353,274,375,262,375,255,369,243,377,239,369,232,365" },
  { alt: "Nicholas", coords: "244,378,255,373,262,378,273,377,269,381,273,388,274,393,272,400,277,409,280,408,302,423,266,454,243,449,239,442,235,435,229,428,226,431,222,425,217,433,210,428,204,427,197,423,241,398,254,385" },
  { alt: "Pendleton", coords: "452,304,492,336,487,346,488,353,481,352,469,383,462,392,459,396,436,388,429,371,415,366,415,355,420,353,415,357,420,350,428,338,431,331,430,323,431,315,437,315,441,309,448,310" }
];

/**
 * Opens a URL in a new tab/window.
 */
function callToOpenURL(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Calls the provider search/retrieve API for the given county, using the
 * fixed CSED query string and the county's approximate lat/long as the POST
 * body inputs, along with the same envId/page/size/radius fields sent by
 * the other provider search/retrieve callers (see results.jsx's fetchResults
 * and its handleEmailResults). Returns the parsed JSON response, or null if
 * the request failed (errors are logged, not thrown, so a failed lookup
 * doesn't crash the county-selection UI).
 *
 * @param {string} county - WV county name (must be a key of COUNTY_COORDINATES)
 * @param {Object} options
 * @param {string|number} options.envId - Environment ID (PlanFeaturesContext's environmentId)
 * @param {number} [options.page=1] - 1-based page number (converted to the API's 0-based `page`)
 * @param {number} [options.size=10] - Page size (capped at 100, same as results.jsx)
 * @param {string|number} [options.radius=25] - Search radius in miles
 * @returns {Promise<Object|null>}
 */
async function fetchCsedProviders(county, { envId, page = 1, size = 10, radius = '25' } = {}) {
  const coords = COUNTY_COORDINATES[county];
  if (!coords) {
    console.warn(`No lat/long found for county "${county}"; skipping CSED provider search.`);
    return null;
  }

  if (!envId) {
    console.warn('No environmentId available; skipping CSED provider search.');
    return null;
  }

  const apiSize = Math.min(size, 100); // Cap API size at 100 to avoid API errors, same as results.jsx

  const searchBody = {
    envId,
    query: CSED_QUERY,
    lat: coords.lat,
    long: coords.long,
    page: page - 1, // API uses 0-based pagination
    size: apiSize,
    radius
  };

  const url = `${config.APIC_BASE_URL_API}${config.SEARCH_API_CONFIG.providerSearchEndpoint}`;

  try {
    console.log('');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🔍 CSED PROVIDER SEARCH API REQUEST');
    console.log('═══════════════════════════════════════════════════════════');
    console.log('County:', county);
    console.log('Method: POST');
    console.log('URL:', url);
    console.log('POST Body:', JSON.stringify(searchBody, null, 2));
    console.log('═══════════════════════════════════════════════════════════');
    console.log('');

    const response = await apicFetch(url, {
      method: 'POST',
      body: JSON.stringify(searchBody)
    });

    if (!response.ok) {
      console.error('❌ CSED PROVIDER SEARCH API ERROR:', {
        county,
        status: response.status,
        statusText: response.statusText,
        url
      });
      return null;
    }

    const data = await response.json();
    console.log('✅ CSED provider search response:', data);
    return data;
  } catch (error) {
    console.error('❌ Error calling CSED provider search API:', error);
    return null;
  }
}

/**
 * CSEDProviderMap
 *
 * React conversion of the original Razor (.vbhtml) markup for the
 * "Children with Serious Emotional Disorder Waiver (CSEDW)" county
 * selection section (dropdown + clickable WV county map).
 *
 * @param {Object} props
 * @param {string} props.globalAssetsPath - Base path for global assets (replaces @GlobalAssetsPath)
 * @param {boolean} [props.visible=false] - Whether the section is shown (replaces display:none)
 * @param {(county: string) => void} props.onFindProvider - Callback invoked with selected county
 *   when the user clicks the map or the "Find a CSED Provider" button.
 * @param {number} [props.page=1] - 1-based page number to send to the provider search API.
 * @param {number} [props.size=10] - Page size to send to the provider search API.
 * @param {string|number} [props.radius=25] - Search radius (miles) to send to the provider search API.
 */
export default function CSEDProviderMap({
  globalAssetsPath = "",
  visible = false,
  onFindProvider,
  page = 1,
  size = 10,
  radius = '25'
}) {
  const { environmentId } = usePlanFeatures();
  const [selectedCounty, setSelectedCounty] = useState(COUNTIES[0]);
  const [hoveredCounty, setHoveredCounty] = useState(null);
  const imgRef = useRef(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });

  const updateScale = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const { width, height } = img.getBoundingClientRect();
    if (width > 0 && height > 0) {
      setScale({
        x: width / BASE_MAP_WIDTH,
        y: height / BASE_MAP_HEIGHT
      });
    }
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const img = imgRef.current;
    // Recalculate once the image has actually loaded (in case it wasn't
    // cached and had 0 size on first mount).
    updateScale();

    if (img && img.complete === false) {
      img.addEventListener("load", updateScale);
    }

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && img) {
      resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(img);
    } else {
      window.addEventListener("resize", updateScale);
    }

    return () => {
      if (img) img.removeEventListener("load", updateScale);
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener("resize", updateScale);
    };
  }, [visible, updateScale]);

  const scaleCoords = (coords) =>
    coords
      .split(",")
      .map((value, index) => {
        const num = parseFloat(value);
        const scaled = index % 2 === 0 ? num * scale.x : num * scale.y;
        return Math.round(scaled * 100) / 100;
      })
      .join(",");

  // Same as scaleCoords, but formatted as "x,y x,y ..." pairs for use as an
  // SVG <polygon> points attribute (used for the hover highlight overlay).
  const scaleCoordsToPoints = (coords) => {
    const nums = coords.split(",").map(Number);
    const points = [];
    for (let i = 0; i < nums.length; i += 2) {
      const x = Math.round(nums[i] * scale.x * 100) / 100;
      const y = Math.round(nums[i + 1] * scale.y * 100) / 100;
      points.push(`${x},${y}`);
    }
    return points.join(" ");
  };

  const handleDropdownChange = (e) => {
    const county = e.target.value;
    setSelectedCounty(county);
    fetchCsedProviders(county, { envId: environmentId, page, size, radius });
  };

  const handleFindProviderClick = () => {
    onFindProvider?.(selectedCounty, "dropdown");
  };

  const handleMapAreaClick = (county) => (e) => {
    e.preventDefault();
    setSelectedCounty(county);
    fetchCsedProviders(county, { envId: environmentId, page, size, radius });
    onFindProvider?.(county, "map");
  };

  const handleMapAreaEnter = (county) => () => {
    setHoveredCounty(county);
  };

  const handleMapAreaLeave = () => {
    setHoveredCounty(null);
  };

  return (
    <div
      className="row mapSection"
      style={{ maxWidth: 916, margin: "auto", display: visible ? "block" : "none" }}
    >
      <div className="row">
        <div className="col-md-6">
          <h1 style={{ fontSize: "3.5rem", marginBottom: "30px" }}>Find a CSED provider</h1>
          <p className="we-update-our-provider-label" style={{ marginBottom: "45px" }}>
            Here, you can find providers if your child takes part in the{" "}
            <a
              href="https://www.aetnabetterhealth.com/westvirginia/csed-waiver.html."
              className="text-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              CSEDW (Children with Serious Emotional Disorder Waiver) program
            </a>
            . These providers support children and teens with serious behavioral or mental health needs.
          </p>

          <p
            style={{
              fontSize: 16,
              fontWeight: 500,
              letterSpacing: 0,
              lineHeight: "25.6px"
            }}
          >
            Choose your county
          </p>

          <p className="we-update-our-provider-label" style={{ marginBottom: 30 }}>
            Use the dropdown menu or the map to choose your county. Then, use the button to find a CSED provider.
          </p>

          <div className="dropdown-container">
            <Dropdown
              id="countyList"
              className="filter-dropdown"
              value={selectedCounty}
              onChange={handleDropdownChange}
              options={COUNTY_OPTIONS}
              aria-label="Choose your county"
            />
          </div>

          <div className="row" style={{ marginTop: 46 }}>
            <div className="col-md-12">
              <button
                type="button"
                name="btnsearch"
                id="btnsearchCA"
                className="all-filters-button"
                style={{ margin: 0 }}
                onClick={handleFindProviderClick}
              >
                Find a CSED Provider
              </button>
            </div>
          </div>

          <p className="we-update-our-provider-label" style={{ marginBottom: 30, marginTop: 30 }}>
            Questions about CSED providers?{" "}
            <a href="mailto:abhwvcsed@aetna.com" target="_blank" title="Opens in New Window" rel="noopener noreferrer">
              Email us
            </a>
            .
          </p>

          <p className="we-update-our-provider-label" style={{ marginBottom: 30 }}>
            Looking for other provider types? Check our{" "}
            <a
              href="javascript: void(0)"
              onClick={() => callToOpenURL("https://qawww.aetnabetterhealth.com/westvirginia/find-provider")}
            >
              find a provider page
            </a>
            .
          </p>
        </div>

        <div className="col-md-6">
          <div className="csed-map-wrapper">
            <img
              ref={imgRef}
              className="csed-map-image"
              src={mapWvImage}
              alt="West Virginia county map"
              useMap="#workmap"
              onLoad={updateScale}
            />
            {hoveredCounty && (
              <svg
                width={imgRef.current?.clientWidth || 0}
                height={imgRef.current?.clientHeight || 0}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: "none"
                }}
              >
                <polygon
                  points={scaleCoordsToPoints(
                    MAP_AREAS.find((area) => area.alt === hoveredCounty)?.coords || ""
                  )}
                  fill="rgba(128, 0, 128, 0.5)"
                  stroke="purple"
                  strokeWidth="1.5"
                />
              </svg>
            )}
            <map name="workmap">
              {MAP_AREAS.map(({ alt, coords }) => (
                <area
                  key={alt}
                  target=""
                  alt={alt}
                  title={alt}
                  href="Javascript: void(0)"
                  coords={scaleCoords(coords)}
                  shape="poly"
                  onClick={handleMapAreaClick(alt)}
                  onMouseEnter={handleMapAreaEnter(alt)}
                  onMouseLeave={handleMapAreaLeave}
                />
              ))}
            </map>
          </div>
        </div>
      </div>
    </div>
  );
}
